/**
 * The physics world: fixed-step integration, sequential impulses, wall
 * constraints, and sleeping so a phone does not cook itself holding a pile still.
 */

import type { Body } from './body.ts';
import type { Manifold } from './collide.ts';
import type { Vec } from './vec.ts';
import { BEHAVIORS } from './body.ts';
import { collide, mayCollide, worldVerts } from './collide.ts';
import { clamp, cross, crossSV, dot, sub } from './vec.ts';

export const FIXED_DT = 1 / 60;
const SOLVER_ITERATIONS = 8;
const PENETRATION_SLOP = 0.6;
const POSITION_CORRECTION = 0.35;
/** Below this approach speed a contact is "resting", not an impact. */
const RESTING_SPEED = 60;
/** Movement below this (px/s and rad/s of real displacement) counts as still. */
const SLEEP_MOVE = 22;
const SLEEP_TURN = 0.5;
const SLEEP_TIME = 0.9;

export interface Impact {
  point: Vec;
  /** Approach speed in px/s — drives sound volume and pitch. */
  speed: number;
  bodyId: number;
  /**
   * What it hit, or -1 for a wall. A ball striking a drawn bar reports the
   * ball as the mover, so the thing that should ring is this one.
   */
  otherId: number;
  /** How big the thing that hit is, so a pile can sound like a marimba. */
  radius: number;
}

export interface WorldOptions {
  gravity?: number;
  width: number;
  height: number;
}

export class World {
  bodies: Body[] = [];
  gravity: number;
  width: number;
  height: number;
  /** 1 = normal speed, 0.3 = slow motion. */
  timeScale = 1;
  /** Floor/wall bounciness — the "Bouncy world" magic raises this. */
  wallRestitution = 0.3;
  /** Impacts recorded during the last step, drained by the sound mixer. */
  impacts: Impact[] = [];

  private accumulator = 0;

  constructor(opts: WorldOptions) {
    this.gravity = opts.gravity ?? 1800;
    this.width = opts.width;
    this.height = opts.height;
  }

  add(body: Body): Body {
    this.bodies.push(body);
    return body;
  }

  remove(id: number): void {
    const i = this.bodies.findIndex((b) => b.id === id);
    if (i >= 0) this.bodies.splice(i, 1);
  }

  clear(): void {
    this.bodies.length = 0;
    this.impacts.length = 0;
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    this.wake();
  }

  wake(): void {
    for (const b of this.bodies) {
      b.asleep = false;
      b.restTime = 0;
    }
  }

  /** Topmost body containing the world point, or null. */
  pick(p: Vec, padding = 12): Body | null {
    for (let i = this.bodies.length - 1; i >= 0; i--) {
      const b = this.bodies[i];
      if (b.invMass === 0) continue;
      const dx = p.x - b.pos.x;
      const dy = p.y - b.pos.y;
      if (dx * dx + dy * dy > (b.radius + padding) * (b.radius + padding)) continue;
      if (containsPoint(b, p, padding)) return b;
    }
    return null;
  }

  /** Advance by real elapsed seconds, running whole fixed steps. */
  update(elapsed: number): void {
    this.impacts.length = 0;
    this.accumulator += Math.min(elapsed, 0.25) * this.timeScale;
    let steps = 0;
    while (this.accumulator >= FIXED_DT && steps < 5) {
      this.step(FIXED_DT);
      this.accumulator -= FIXED_DT;
      steps++;
    }
    if (steps === 5) this.accumulator = 0;
  }

  step(dt: number): void {
    const bodies = this.bodies;

    for (const b of bodies) {
      if (b.wobble !== 0) b.wobble *= 0.88;
      if (b.lastImpact > 0) b.lastImpact = Math.max(0, b.lastImpact - dt);
      if (b.invMass === 0 || b.asleep || b.held) continue;
      const t = BEHAVIORS[b.behavior];
      b.vel.y += this.gravity * t.gravity * dt;
      b.vel.x *= t.damping;
      b.vel.y *= t.damping;
      if (t.spin !== 0) b.angVel += (t.spin - b.angVel) * Math.min(1, dt * 4);
      else b.angVel *= t.angularDamping;
    }

    // Broad + narrow phase.
    const manifolds: Manifold[] = [];
    for (let i = 0; i < bodies.length; i++) {
      for (let j = i + 1; j < bodies.length; j++) {
        const a = bodies[i];
        const b = bodies[j];
        if (a.invMass === 0 && b.invMass === 0) continue;
        if (a.asleep && b.asleep) continue;
        if (!mayCollide(a, b)) continue;
        const m = collide(a, b);
        if (m) manifolds.push(m);
      }
    }

    for (const m of manifolds) {
      if (m.a.asleep && !m.b.asleep) m.a.asleep = false;
      if (m.b.asleep && !m.a.asleep) m.b.asleep = false;
      this.recordImpact(m);
    }

    for (let it = 0; it < SOLVER_ITERATIONS; it++) {
      for (const m of manifolds) resolveManifold(m);
      for (const b of bodies) this.resolveWalls(b, it === 0);
    }

    // Positional correction runs once per step, after the impulses. Doing it
    // inside the iteration loop compounds into a visible bob that never settles.
    for (const m of manifolds) correctPositions(m);
    for (const b of bodies) this.correctWalls(b);

    for (const b of bodies) {
      if (b.invMass === 0 || b.asleep) continue;
      if (!b.held) {
        b.pos.x += b.vel.x * dt;
        b.pos.y += b.vel.y * dt;
        b.angle += b.angVel * dt;
      }
      this.clampInside(b);

      // Sleep on net displacement across the whole step (integration AND the
      // solver's corrections). A body resting on the floor keeps a sawtooth
      // velocity — gravity in, impulse out — so a velocity threshold, or one
      // that ignores the corrections, would never let a pile settle.
      const moved = Math.hypot(b.pos.x - b.restX, b.pos.y - b.restY);
      const turned = Math.abs(b.angle - b.restAngle);
      b.restX = b.pos.x;
      b.restY = b.pos.y;
      b.restAngle = b.angle;
      const still = moved < SLEEP_MOVE * dt && turned < SLEEP_TURN * dt;
      const canSleep = BEHAVIORS[b.behavior].spin === 0 && BEHAVIORS[b.behavior].gravity >= 0;
      b.restTime = still && !b.held ? b.restTime + dt : 0;
      if (canSleep && b.restTime > SLEEP_TIME) {
        b.asleep = true;
        b.vel.x = 0;
        b.vel.y = 0;
        b.angVel = 0;
      }
    }
  }

  private recordImpact(m: Manifold): void {
    const contact = m.contacts[0];
    const ra = sub(contact, m.a.pos);
    const rb = sub(contact, m.b.pos);
    const va = { x: m.a.vel.x - m.a.angVel * ra.y, y: m.a.vel.y + m.a.angVel * ra.x };
    const vb = { x: m.b.vel.x - m.b.angVel * rb.y, y: m.b.vel.y + m.b.angVel * rb.x };
    const approach = dot(sub(vb, va), m.normal);
    if (approach < -60) {
      const speed = -approach;
      const hitter = m.a.invMass > 0 ? m.a : m.b;
      const other = hitter === m.a ? m.b : m.a;
      this.impacts.push({ point: contact, speed, bodyId: hitter.id, otherId: other.id, radius: hitter.radius });
      const punch = Math.min(0.5, speed / 2400);
      m.a.wobble = Math.max(m.a.wobble, punch);
      m.b.wobble = Math.max(m.b.wobble, punch);
      m.a.lastImpact = 0.18;
      m.b.lastImpact = 0.18;
    }
  }

  /** Walls are handled as per-vertex half-space constraints — cheap and stable. */
  private resolveWalls(body: Body, record: boolean): void {
    if (body.invMass === 0 || body.asleep) return;
    const verts = worldVerts(body);
    for (const wall of this.wallPlanes()) {
      let deepest: Vec | null = null;
      let depth = 0;
      for (const v of verts) {
        const d = dot(wall.n, v) - wall.limit;
        if (d < -depth) {
          depth = -d;
          deepest = v;
        }
      }
      if (!deepest || depth <= 0) continue;

      const r = sub(deepest, body.pos);
      const vAt = { x: body.vel.x - body.angVel * r.y, y: body.vel.y + body.angVel * r.x };
      const vn = dot(vAt, wall.n);
      if (vn < 0) {
        if (record && vn < -90) {
          this.impacts.push({ point: deepest, speed: -vn, bodyId: body.id, otherId: -1, radius: body.radius });
          body.wobble = Math.max(body.wobble, Math.min(0.55, -vn / 2200));
          body.lastImpact = 0.18;
        }
        const traits = BEHAVIORS[body.behavior];
        // No bounce at all below a real impact speed, or a resting shape
        // trampolines forever on its own contact impulse.
        const e = vn < -RESTING_SPEED ? Math.min(0.95, Math.max(traits.restitution, this.wallRestitution)) : 0;
        const rn = cross(r, wall.n);
        const invEff = body.invMass + rn * rn * body.invInertia;
        const jn = (-(1 + e) * vn) / Math.max(invEff, 1e-6);
        applyAt(body, { x: wall.n.x * jn, y: wall.n.y * jn }, r);

        // Friction along the wall.
        const tangent = { x: -wall.n.y, y: wall.n.x };
        const vAt2 = { x: body.vel.x - body.angVel * r.y, y: body.vel.y + body.angVel * r.x };
        const vt = dot(vAt2, tangent);
        const rt = cross(r, tangent);
        const invEffT = body.invMass + rt * rt * body.invInertia;
        let jt = -vt / Math.max(invEffT, 1e-6);
        const maxF = Math.abs(jn) * traits.friction;
        jt = clamp(jt, -maxF, maxF);
        applyAt(body, { x: tangent.x * jt, y: tangent.y * jt }, r);
      }

      // Resting on the floor always leaves a hair of penetration. Waking on
      // that alone would mean nothing ever sleeps — only a real shove counts.
      if (depth > PENETRATION_SLOP * 6) {
        body.restTime = 0;
        body.asleep = false;
      }
    }
  }

  /** Push a body out of the walls once per step (see the note at the call site). */
  private correctWalls(body: Body): void {
    if (body.invMass === 0 || body.asleep) return;
    for (const wall of this.wallPlanes()) {
      let depth = 0;
      for (const v of worldVerts(body)) {
        const d = dot(wall.n, v) - wall.limit;
        if (d < -depth) depth = -d;
      }
      if (depth <= PENETRATION_SLOP) continue;
      const push = (depth - PENETRATION_SLOP) * POSITION_CORRECTION;
      body.pos.x += wall.n.x * push;
      body.pos.y += wall.n.y * push;
    }
  }

  private wallPlanes(): Array<{ n: Vec; limit: number }> {
    return [
      { n: { x: 0, y: -1 }, limit: -this.height },
      { n: { x: 0, y: 1 }, limit: 0 },
      { n: { x: -1, y: 0 }, limit: -this.width },
      { n: { x: 1, y: 0 }, limit: 0 },
    ];
  }

  /** Last-resort guard so nothing can tunnel out of view and be lost forever. */
  private clampInside(body: Body): void {
    const r = body.radius;
    if (body.pos.x < -r * 2 || body.pos.x > this.width + r * 2 || body.pos.y > this.height + r * 3 || body.pos.y < -r * 6) {
      body.pos.x = clamp(body.pos.x, r, this.width - r);
      body.pos.y = clamp(body.pos.y, -r * 4, this.height - r);
      body.vel.x *= 0.2;
      body.vel.y = Math.min(body.vel.y, 0);
    }
  }
}

function applyAt(body: Body, impulse: Vec, r: Vec): void {
  body.vel.x += impulse.x * body.invMass;
  body.vel.y += impulse.y * body.invMass;
  body.angVel += cross(r, impulse) * body.invInertia;
}

function resolveManifold(m: Manifold): void {
  const { a, b, normal, contacts } = m;
  const invSum = a.invMass + b.invMass;
  if (invSum === 0) return;
  const e = Math.min(BEHAVIORS[a.behavior].restitution, BEHAVIORS[b.behavior].restitution);
  const mu = Math.sqrt(BEHAVIORS[a.behavior].friction * BEHAVIORS[b.behavior].friction);

  for (const contact of contacts) {
    const ra = sub(contact, a.pos);
    const rb = sub(contact, b.pos);
    const rel = relativeVelocity(a, b, ra, rb);
    const vn = dot(rel, normal);
    if (vn > 0) continue;

    const rnA = cross(ra, normal);
    const rnB = cross(rb, normal);
    const invEff = invSum + rnA * rnA * a.invInertia + rnB * rnB * b.invInertia;
    if (invEff <= 1e-9) continue;

    const bounce = vn < -140 ? e : 0;
    const jn = (-(1 + bounce) * vn) / invEff / contacts.length;
    const impulse = { x: normal.x * jn, y: normal.y * jn };
    applyAt(a, { x: -impulse.x, y: -impulse.y }, ra);
    applyAt(b, impulse, rb);

    const tangent = { x: -normal.y, y: normal.x };
    const rel2 = relativeVelocity(a, b, ra, rb);
    const vt = dot(rel2, tangent);
    const rtA = cross(ra, tangent);
    const rtB = cross(rb, tangent);
    const invEffT = invSum + rtA * rtA * a.invInertia + rtB * rtB * b.invInertia;
    if (invEffT <= 1e-9) continue;
    let jt = -vt / invEffT / contacts.length;
    const maxF = Math.abs(jn) * mu;
    jt = clamp(jt, -maxF, maxF);
    const fImpulse = { x: tangent.x * jt, y: tangent.y * jt };
    applyAt(a, { x: -fImpulse.x, y: -fImpulse.y }, ra);
    applyAt(b, fImpulse, rb);
  }
}

function relativeVelocity(a: Body, b: Body, ra: Vec, rb: Vec): Vec {
  const va = { x: a.vel.x, y: a.vel.y };
  const vb = { x: b.vel.x, y: b.vel.y };
  const wa = crossSV(a.angVel, ra);
  const wb = crossSV(b.angVel, rb);
  return { x: vb.x + wb.x - (va.x + wa.x), y: vb.y + wb.y - (va.y + wa.y) };
}

function correctPositions(m: Manifold): void {
  const invSum = m.a.invMass + m.b.invMass;
  if (invSum === 0) return;
  const push = (Math.max(0, m.depth - PENETRATION_SLOP) / invSum) * POSITION_CORRECTION;
  m.a.pos.x -= m.normal.x * push * m.a.invMass;
  m.a.pos.y -= m.normal.y * push * m.a.invMass;
  m.b.pos.x += m.normal.x * push * m.b.invMass;
  m.b.pos.y += m.normal.y * push * m.b.invMass;
}

/** Point-in-body test in the body's local frame, with a fat-finger padding. */
export function containsPoint(body: Body, p: Vec, padding = 0): boolean {
  const c = Math.cos(-body.angle);
  const s = Math.sin(-body.angle);
  const dx = p.x - body.pos.x;
  const dy = p.y - body.pos.y;
  const local = { x: dx * c - dy * s, y: dx * s + dy * c };
  const poly = body.verts;
  if (poly.length < 3) return Math.hypot(local.x, local.y) <= padding + 10;

  let inside = true;
  let sign = 0;
  for (let i = 0; i < poly.length; i++) {
    const va = poly[i];
    const vb = poly[(i + 1) % poly.length];
    const edge = sub(vb, va);
    const side = cross(edge, sub(local, va));
    const norm = Math.hypot(edge.x, edge.y) || 1;
    const signed = side / norm;
    if (sign === 0) sign = signed >= 0 ? 1 : -1;
    if (signed * sign < -padding) {
      inside = false;
      break;
    }
  }
  return inside;
}
